'use client';

// Профиль на «Настройках»: центрированная карточка (аватар → имя → имейл → Telegram),
// клик по любому месту открывает модалку редактирования. В модалке редактируются ТОЛЬКО
// фото (клик по нему — кроппер вложенным экраном) и имя; имейл и Telegram — строки-показ
// в карточке-списке (DList/DRow — язык кабинета). «Сохранить» — ✓ в шапке модалки.
// Внизу — «Передать кабинет» (заказ владельца 2026-07-19): ссылка-приглашение, по
// которой новый человек подтверждает свободный имейл кодом и Telegram ботом — и
// кабинет перевешивается на него целиком (механика — lib/cabinet-transfer.ts).
import { webOnly, readPhoneLook, usePhoneLook } from './layout-mode';
import { CABINET_TRANSFER_HIDDEN } from '@/lib/hidden-features';
import { ACCOUNT_DELETE_GRACE_DAYS } from '@scriptera/db/account-delete';
import { KBareButton, KButton, KListGroup } from '@scriptera/ui/kit';
import { useEffect, useRef, useState } from 'react';
import { CopyBtn } from './copy-btn';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/lib/cabinet-nav';
import { bumpAvatar, Cropper, ProfileAvatar, saveAvatarDataUrl } from './avatar';
import { ConfirmInline } from './confirm-inline';
import { useSlidePanel } from './native-layer-host';
import { nativeBridge, nativeMethod, useNativeApp, type NativeAccountPage } from '@/lib/native-app';
import { accountRoleHex } from '@/lib/account-native';
import { askNativeConfirm, hasNativeConfirm } from './confirm-sheet';
import { LogoutButton, useLogout } from './logout-button';
import { HERO_ICONS, openHeroAsk } from './hero-ask-sheet';
import { isStepUp, StepUpDialog } from './step-up-dialog';
import { DCenter, DError, DFine, DGroup, DHint } from './drawer-kit';
import { FBlock, FClearField, FNotifyList, FPane, KInput } from './form-kit';
import { askConfirm } from './ask';
import { Sk } from './skeleton';
import { HeroGradientOnly } from './settings-hero';
import { usePersistentData } from '@/lib/use-persistent-data';

const AVATAR_URL = '/api/cabinet/profile/avatar';

const iconProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};
const MailIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="M3.5 7l8.5 6 8.5-6" />
  </svg>
);
const TgIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <path d="M21.5 4.5L2.8 11.7c-.8.3-.75 1.4.05 1.7l4.6 1.6 1.7 5.2c.25.75 1.2.9 1.7.3l2.4-2.9 4.7 3.4c.6.45 1.5.1 1.65-.65l3-14.6c.15-.85-.65-1.55-1.4-1.25z" />
    <path d="M9.2 14.9l9.4-8.6" />
  </svg>
);
const CameraIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.2l1.1-1.7A1.5 1.5 0 0 1 10.05 3.6h3.9a1.5 1.5 0 0 1 1.25.7L16.3 6h1.2A2.5 2.5 0 0 1 20 8.5v8A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5z" />
    <circle cx="12" cy="12.5" r="3.4" />
  </svg>
);
const UserIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <circle cx="12" cy="8.5" r="3.8" />
    <path d="M4.5 20c1.2-3.6 4-5.5 7.5-5.5s6.3 1.9 7.5 5.5" />
  </svg>
);
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <path d="M6.6 3.8h2.2l1.5 3.7-1.9 1.5a12.5 12.5 0 0 0 6.6 6.6l1.5-1.9 3.7 1.5v2.2a1.8 1.8 0 0 1-1.9 1.8A15.9 15.9 0 0 1 4.8 5.7a1.8 1.8 0 0 1 1.8-1.9z" />
  </svg>
);

export interface ProfileInfo {
  name: string; // отображаемое имя (fallback от имейла применён на сервере)
  email: string;
  phone: string | null; // подтверждён через Telegram
  /** Телеграм-аккаунт (@ник или имя из tg_users); null = не привязан. */
  tgName: string | null;
  initial: string;
  hasAvatar: boolean;
  /** Метка аккаунта для адреса фото (короткий хэш id, считает layout кабинета):
   *  свой адрес картинки у каждого аккаунта — иначе суточный кэш браузера показывал бы
   *  после перелогина фото прошлого. Строит адрес avatarSrc (avatar.tsx). */
  avatarTag: string;
  /** Своя учётка (администратор или почта на нашем домене, lib/our-accounts.ts): в
   *  «Аккаунте» iOS-приложения ей видна строка «Сервер» — переключатель прод / staging. */
  serverSwitch?: boolean;
}

/** Открыть редактор профиля ШТОРКОЙ (фото, имя, контакты, «Передать кабинет»).
 *  Общая рука для строки «Профиль» в списке «Аккаунта» и кнопки нативной шапки. */
export function useOpenProfile(user: ProfileInfo): () => void {
  const t = useTranslations('cabinet.settings');
  // Приложение — родной боковой слой (стопка: передача/кроппер выезжают ПОВЕРХ редактора,
  // он остаётся под ними, «назад» снимает слой); веб — тот же порядок уровнями drawer (с 05.10).
  const drawer = useSlidePanel();
  const saveRef = useRef<(() => void) | null>(null);
  const cropRef = useRef<(() => void) | null>(null);

  // Возврат из под-экрана — снятие его уровня: редактор жив под ним и в приложении, и в вебе
  // (ТЗ «Причины» П1, 05.10.2026: drawer кладёт под-экран уровнем, а не подменяет корень).
  // Прежний веб-reopen редактора (onBack) положил бы теперь его дубль поверх — его больше нет.
  // ОДНА шторка на профиль (владелец 2026-07-25): фото, имя и контакты вместе —
  // отдельного экрана аватарки больше нет, тап по фото сразу ведёт в кроппер.
  const openEditor = () => {
    drawer.open({
      // Страница-герой (владелец 2026-07-24): в приложении титул шапки пуст, имя
      // страницы — в самом герое; градиент — от акцента.
      title: '',

      body: (
        <>
          {/* Герой (значок-человечек и слово «Профиль») УБРАН — вместо него сама
              фотография (владелец 2026-07-25); от героя остаётся только градиент. */}
          <HeroGradientOnly color="var(--violet)" />
          <ProfileEditor
            user={user}
            saveRef={saveRef}
            onOpenTransfer={openTransfer}
            onPickFile={openCropper}
          />
          {/* ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 К5, кадр iOS K02-profile): «Выйти» и «Удалить
              учётную запись» — внизу листа профиля белыми капсулами с красным словом. */}
          {readPhoneLook() ? (
            <>
              <div className="acc-logout acc-logout--wide">
                <LogoutButton wide />
              </div>
              <DeleteAccountBlock email={user.email} />
            </>
          ) : null}
        </>
      ),
    });
  };
  const openTransfer = () => {
    drawer.open({
      title: t('transferTitle'),
      // «Передать кабинет» — вертикальная шторка (нижний лист) ВЕЗДЕ: направление шторки
      // одно на мобильный сайт и приложение (владелец 2026-07-21, IMG_0578).
      sheet: true,
      body: <CabinetTransferPanel />,
    });
  };
  // Кроппер выезжает ПОВЕРХ профиля уровнем стопки; «назад»/сохранение снимают только его —
  // редактор под ним цел (и в приложении, и в браузере на любой ширине, ТЗ «Причины» П1).
  // Телефон в браузере: лист-стопка с крестиком (кадр iOS K02-profile-photo-crop).
  const backToProfile = () => drawer.close();
  const openCropper = (file: File) => {
    drawer.open({
      title: t('avatar'),
      // Кроппер — вертикальный лист в браузере (iOS PhotoCropSheet, ТЗ WEB-LOOK Э2.5).
      ...webOnly({ sheet: true }),
      // Телефон в браузере — лист-стопка с «×» и галочкой (К4, кадр iOS K02-profile-photo-crop).
      ...(readPhoneLook() ? {} : {}),
      actionLabel: t('avatarSave'),
      actionIcon: 'checkmark',
      onAction: () => cropRef.current?.(),
      body: <Cropper file={file} saveRef={cropRef} onDone={backToProfile} />,
    });
  };

  return openEditor;
}

/** ГЕРОЙ-БЛОК профиля наверху страницы «Аккаунт» (владелец 2026-08-07: «я не разрешал
 *  убирать мой Hero блок» — возвращён ровно тем же, каким был на «Настройках»):
 *  крупное фото, имя, почта и Telegram колонкой по центру; тап по любому месту
 *  открывает редактор профиля.
 *  Класс .profcard держим на текстовой кнопке: её кликает программно нативная шапка
 *  приложения (shell.tsx, ключ top:profile-edit) и кружок узкой веб-шапки. */
export function ProfileCard({ user }: { user: ProfileInfo }) {
  const t = useTranslations('cabinet.settings');
  const openEditor = useOpenProfile(user);
  return (
    <div className="profwrap">
      <KBareButton
        className="profava"
        aria-label={t('avatarTap')}
        title={t('avatarTap')}
        onClick={openEditor}
      >
        {/* 88 — герой родного «Аккаунта» (accountHeroAvatar; паритет 09.09.2026). */}
        <ProfileAvatar initial={user.initial} tag={user.avatarTag} size={88} />
      </KBareButton>
      <KBareButton className="profcard" onClick={openEditor}>
        <b className="profname">{user.name}</b>
        <div className="profmail">{user.email}</div>
        <div className="proftg">{user.phone ? `Telegram · ${user.phone}` : t('telegramNone')}</div>
      </KBareButton>
    </div>
  );
}

/** Профиль СТРАНИЦЕЙ (владелец 2026-08-10: «Профиль» снова первый пункт «Аккаунта», и
 *  на широком экране он же — корень раздела): то же тело, что у шторки, но стоит прямо
 *  в поле контента. Канон UI-TEMPLATE: есть вторая колонка — пункт открывает содержимое
 *  СПРАВА, шторкой оттуда не открываем. Под-экраны (кроппер фото и «Передать кабинет») —
 *  по-прежнему слой поверх страницы: это не пункты меню, а шаги внутри неё. */
export function ProfilePanel({ user }: { user: ProfileInfo }) {
  const t = useTranslations('cabinet.settings');
  const drawer = useSlidePanel();
  const native = useNativeApp();
  const saveRef = useRef<(() => void) | null>(null);
  const cropRef = useRef<(() => void) | null>(null);
  // Возврата «назад в профиль» тут нет и быть не должно: родитель — сама страница, она
  // никуда не делась; слой просто снимается (в вебе — закрывается drawer).
  const openTransfer = () =>
    drawer.open({
      title: t('transferTitle'),
      // Вертикальная шторка ВЕЗДЕ — как у того же входа из шторки профиля.
      sheet: true,
      body: <CabinetTransferPanel />,
    });
  const openCropper = (file: File) =>
    drawer.open({
      title: t('avatar'),
      // Кроппер — вертикальный лист в браузере (iOS PhotoCropSheet, ТЗ WEB-LOOK Э2.5).
      ...webOnly({ sheet: true }),
      ...(readPhoneLook() ? {} : {}),
      actionLabel: t('avatarSave'),
      actionIcon: 'checkmark',
      onAction: () => cropRef.current?.(),
      body: <Cropper file={file} saveRef={cropRef} onDone={() => drawer.close()} />,
    });
  // ТЕЛЕФОН В БРАУЗЕРЕ — КАК РОДНОЙ «ПРОФИЛЬ» (ТЗ WEB-PARITY-3 К5, кадр iOS K02-profile):
  // «Выйти» и «Удалить учётную запись» — белые капсулы с красным словом внизу страницы.
  const phoneWeb = usePhoneLook();
  return (
    <>
      <ProfileEditor
        user={user}
        saveRef={saveRef}
        onOpenTransfer={openTransfer}
        onPickFile={openCropper}
      />
      {/* «Выйти» — ВНИЗУ ПРОФИЛЯ В ПРИЛОЖЕНИИ (владелец 01.09.2026): у родного корня
          «Аккаунта» кнопки выхода нет — тап по герою ведёт сюда, выход живёт здесь.
          В вебе на широком экране кнопка остаётся под списком корня раздела (tab-stack);
          на телефоне — здесь же, как в iOS (К5). */}
      {(native || phoneWeb) && (
        <div className={`acc-logout${phoneWeb ? ' acc-logout--wide' : ''}`}>
          <LogoutButton {...(phoneWeb ? { wide: true } : {})} />
        </div>
      )}
      {/* «Удалить учётную запись» — САМЫМ НИЗОМ раздела учётной записи (ТЗ П127.1,
          требование Apple 5.1.1(v): регистрация в приложении есть — значит и удаление
          обязано быть на виду). Данные не стираются: закрывается вход. */}
      <DeleteAccountBlock email={user.email} />
    </>
  );
}

/** «УДАЛИТЬ УЧЁТНУЮ ЗАПИСЬ» (ТЗ П127.1, владелец 18.09.2026). Вопрос — ПОЛУШТОРКА С ГЕРОЕМ
 *  (владелец 23.10.2026, hero-ask-sheet.tsx): заголовок, текст «как всё работает», «Нет, не
 *  удалять» — акцентная капсула, «Да, удалить» — спокойная надпись; один в один с приложением
 *  (app-pages/account.ts, `ask` со стилем hero). «Да» ставит отметку ручкой /api/user/delete
 *  (почта учётной записи уходит телом, как из приложения; прежнее поле ввода почты 13.10 снято
 *  вместе с окном), гасит сессии и уводит на вход с плашкой про день закрытия. Данные
 *  пользователя и его боты не трогаются. */
function DeleteAccountBlock({ email }: { email: string }) {
  const t = useTranslations('cabinet.settings');
  const tc = useTranslations('cabinet');
  const { logout } = useLogout();
  const panel = useSlidePanel();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  // Сессия по ссылке с автовходом удалить учётку не может (403 step_up, FUNNEL-TZ §5.2 п.7).
  const [stepUp, setStepUp] = useState(false);
  const run = async () => {
    setBusy(true);
    setErr(false);
    try {
      const res = await fetch('/api/user/delete', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = (await res.json().catch(() => ({ ok: false }))) as {
        ok?: boolean;
        date?: string;
        error?: string;
      };
      if (isStepUp(res, data)) {
        setBusy(false);
        setStepUp(true);
        return;
      }
      if (!data.ok || !data.date) {
        setErr(true);
        setBusy(false);
        return;
      }
      // Выход — ТОЙ ЖЕ рукой, что кнопка «Выйти» (второго выхода в кабинете нет):
      // она гасит куку, чистит кэш устройства и снимает родное убранство.
      await logout({ to: `/login?deleted=${encodeURIComponent(data.date)}` });
    } catch {
      setErr(true);
      setBusy(false);
    }
  };
  const ask = () =>
    openHeroAsk(panel, {
      icon: HERO_ICONS.deleteAccount,
      title: t('deleteAccountTitle'),
      text: t('deleteAccountHow', { days: ACCOUNT_DELETE_GRACE_DAYS }),
      actions: [
        { key: 'stay', label: t('deleteAccountStay'), tone: 'primary', onPick: () => {} },
        { key: 'go', label: t('deleteAccountGo'), tone: 'plain', onPick: () => void run() },
      ],
      closeLabel: tc('cancel'),
      // Четыре абзаца текста — лист выше полулиста (0.52), иначе «Да, удалить» уходит под кромку.
      detent: 0.66,
    });

  // КНОПКА НЕ ЯРКАЯ (§3.6 п.1): строка-ссылка серым в самом низу профиля — тот же вид и размер,
  // что «Выйти» (телефон — широкая капсула как у «Выйти», широкий экран — текст-ссылка).
  const phoneWeb = usePhoneLook();
  return (
    <div className={`acc-danger${phoneWeb ? ' acc-danger--wide' : ''}`}>
      <KBareButton
        type="button"
        className={phoneWeb ? 'k-btn k-btn--block k-btn--action prof-wide-btn logout' : 'btn sm logout'}
        onClick={ask}
        disabled={busy}
      >
        {t('deleteAccount')}
      </KBareButton>
      {err ? <DError>{t('deleteAccountError')}</DError> : null}
      <StepUpDialog open={stepUp} onOpenChange={setStepUp} />
    </div>
  );
}

// Шторка аватарки (владелец 2026-07-23: отдельный этап по тапу на фото): крупное фото —
// тап меняет (кроппер поверх), ниже «Убрать фото» с подтверждением.
/** Фото профиля ВНУТРИ единой шторки (владелец 2026-07-25): тап — выбрать файл
 *  (кроппер выезжает поверх), ниже «Убрать фото» с подтверждением. */
function AvatarBlock({
  user,
  onPickFile,
}: {
  user: ProfileInfo;
  onPickFile: (file: File) => void;
}) {
  const t = useTranslations('cabinet.settings');
  const fileRef = useRef<HTMLInputElement>(null);
  const [hasAvatar, setHasAvatar] = useState(user.hasAvatar);
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 К5, кадр iOS K02-profile): фото 88 (accountHeroAvatar
  // приложения; прежние 120 — до сверки с кадром) с серым кружком-камерой 36, заголовок
  // «Профиль» 22, подводка «Нажмите на фото, чтобы заменить», белая широкая «Убрать фото»
  // (вопрос — окном по центру).
  const phoneWeb = usePhoneLook();
  const removePhoto = async () => {
    const r = await fetch(AVATAR_URL, { method: 'DELETE' })
      .then((res) => res.json())
      .catch(() => ({ ok: false }));
    if (r.ok) {
      bumpAvatar();
      setHasAvatar(false);
    }
  };

  return (
    <DGroup>
      <DCenter>
        <KBareButton
          className={`profedit-pick${phoneWeb ? ' profedit-pick--ios' : ''}`}
          aria-label={t('avatarTap')}
          title={t('avatarTap')}
          onClick={() => fileRef.current?.click()}
        >
          <ProfileAvatar initial={user.initial} tag={user.avatarTag} size={phoneWeb ? 88 : 168} />
          <span className="profedit-badge" aria-hidden>
            <CameraIcon />
          </span>
        </KBareButton>
        {phoneWeb ? (
          <div className="prof-hero-t">
            <h2 className="prof-hero-h">{t('profileTitle')}</h2>
            <p className="prof-hero-m">{t('avatarTap')}</p>
          </div>
        ) : null}
        {phoneWeb && hasAvatar ? (
          <KButton
            action
            className="prof-wide-btn prof-wide-btn--ink"
            onClick={() => {
              void askConfirm({
                question: t('avatarRemoveQ'),
                confirmLabel: t('avatarRemove'),
                destructive: true,
              }).then((yes) => {
                if (yes) void removePhoto();
              });
            }}
          >
            {t('avatarRemove')}
          </KButton>
        ) : null}
        {/* Подтверждение — ТОТ ЖЕ компонент и вид, что у «Выхода» (владелец 2026-07-25):
            вопрос слева, кнопки «Да»/«Нет». */}
        {hasAvatar && !phoneWeb && (
          <ConfirmInline
            label={t('avatarRemove')}
            question={t('avatarRemoveQ')}
            confirmLabel={t('avatarRemove')}
            triggerClassName="profedit-remove"
            onConfirm={() => void removePhoto()}
          />
        )}
      </DCenter>
      <KInput
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPickFile(f);
        }}
      />
    </DGroup>
  );
}

/** «Так нельзя»: строка коротко трясётся + вибро-отклик (владелец 2026-07-25). */
function shakeRow(el: HTMLElement | null): void {
  if (!el) return;
  el.classList.remove('row-shake');
  // reflow, иначе повторный тап не перезапустит анимацию
  void el.offsetWidth;
  el.classList.add('row-shake');
  window.setTimeout(() => el.classList.remove('row-shake'), 420);
  if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
  else navigator.vibrate?.(30);
}

/** Строку набора (FNotifyList) своего ref не даёт — трясём её по порядку внутри пузыря.
 *  ДОКЛАД ПРИЁМЩИКУ: набору не хватает ссылки на элемент строки (onOpen без аргумента);
 *  как только она появится, этот поиск по DOM уходит. */
function shakeProfileRow(index: number): void {
  const rows = document.querySelectorAll<HTMLElement>('.prof-rows .fk-nlist-row');
  shakeRow(rows[index] ?? null);
}

/** Шторка правки одного поля: автофокус, открытая клавиатура, × слева и ✓ справа. */
function FieldSheet({
  initial,
  placeholder,
  onDone,
}: {
  initial: string;
  placeholder: string;
  onDone: (v: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [v, setV] = useState(initial);
  useEffect(() => {
    // Клавиатура должна подняться сразу — фокус после первой отрисовки листа.
    const id = window.setTimeout(() => ref.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, []);
  useEffect(() => {
    fieldSheetValue = () => v;
  }, [v]);
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 К3, кадр iOS K02-profile-name-edit): белая карточка-поле
  // 72 со значением и серым «×» очистки справа; на широком — как было.
  const phone = readPhoneLook();
  if (phone) {
    return (
      <KListGroup className="prof-name-field">
        <FClearField
          ref={ref}
          value={v}
          maxLength={80}
          placeholder={placeholder}
          autoCapitalize="words"
          onChange={setV}
          onEnter={() => onDone(v)}
        />
      </KListGroup>
    );
  }
  return (
    <DGroup>
      <KInput
        ref={ref}
        className="fld-solo"
        value={v}
        maxLength={80}
        placeholder={placeholder}
        autoCapitalize="words"
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onDone(v);
        }}
      />
    </DGroup>
  );
}
/** Текущее значение поля шторки — читает галочка шапки (кнопка живёт вне тела). */
let fieldSheetValue: (() => string) | null = null;

/** ЗАПИСЬ ИМЕНИ ПРОФИЛЯ — ОДНА РУКА на веб-шторку правки и на РОДНОЕ поле приложения
 *  (accountSet, docs/NATIVE-ACCOUNT-PLAN.md этап Г): второй записи настройки в кабинете
 *  не заводим. Возвращает «получилось» — показывать ошибку и занятость решает звонящий. */
export function useSaveProfileName(): (name: string) => Promise<boolean> {
  const router = useRouter();
  return async (value: string): Promise<boolean> => {
    const nm = value.trim();
    if (!nm) return false;
    const r = await fetch('/api/cabinet/profile', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name: nm }),
    })
      .then((res) => res.json() as Promise<{ ok?: boolean }>)
      .catch(() => ({ ok: false }));
    if (!r.ok) return false;
    router.refresh(); // серверная карточка профиля перечитает users
    return true;
  };
}

function ProfileEditor({
  user,
  saveRef,
  onOpenTransfer,
  onPickFile,
}: {
  user: ProfileInfo;
  saveRef: React.MutableRefObject<(() => void) | null>;
  onOpenTransfer: () => void;
  /** Выбран файл фото — родитель открывает кроппер поверх этой же шторки. */
  onPickFile: (file: File) => void;
}) {
  const t = useTranslations('cabinet.settings');
  const saveName = useSaveProfileName();
  const drawer = useSlidePanel(); // close: в приложении снимает слой, в вебе закрывает drawer
  // Телефон в браузере (К5): значения строк — справа серым в одну строку, как в iOS.
  const phoneWeb = usePhoneLook();
  const side = (v: string) => (phoneWeb ? { value: v } : { summary: v });
  const [name, setName] = useState(user.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  /** Тап по «Имени» — ГОРИЗОНТАЛЬНАЯ шторка правки (владелец 2026-07-25: «лучше
   *  горизонтальной»), сразу с фокусом в поле и поднятой клавиатурой. */
  const editName = () =>
    drawer.open({
      title: t('profileName'),
      actionIcon: 'checkmark',
      actionLabel: t('avatarSave'),
      onAction: () => {
        const next = (fieldSheetValue?.() ?? name).trim();
        drawer.close();
        if (next && next !== name) void save(next);
      },
      body: (
        <FieldSheet
          initial={name}
          placeholder={t('profileName')}
          onDone={(v) => {
            const next = v.trim();
            drawer.close();
            if (next && next !== name) void save(next);
          }}
        />
      ),
    });

  /** Сохранить имя на сервере. Зовёт галочка ШТОРКИ ПРАВКИ (в шапке профиля её больше
   *  нет — владелец 2026-07-25: она ни за что не отвечала). Сама запись — общей рукой
   *  (useSaveProfileName): ею же пишет родное поле приложения. */
  const save = async (value?: string) => {
    if (busy) return;
    const nm = (value ?? name).trim();
    if (!nm) {
      setErr(true);
      return;
    }
    setBusy(true);
    setErr(false);
    const ok = await saveName(nm);
    setBusy(false);
    if (ok) {
      setName(nm);
      return;
    }
    setErr(true);
  };
  // Ссылка на свежий save остаётся для внешних вызовов (замыкание — каждый рендер).
  useEffect(() => {
    saveRef.current = () => void save();
  });

  // Аватарки здесь больше нет (владелец 2026-07-23): фото редактируется отдельным
  // этапом по тапу на аватар в «Настройках». Тут — имя и контакты.
  return (
    <DGroup>
      {/* Ошибка — красным сразу под шапкой (стандарт модалок, владелец 2026-07-23). */}
      {err && <DError>{t('profileSaveError')}</DError>}
      {/* Фото — ЗДЕСЬ ЖЕ (владелец 2026-07-25: профиль и аватарка одной шторкой). */}
      <AvatarBlock user={user} onPickFile={onPickFile} />
      {/* Правка поля — ШТОРКОЙ с автофокусом и открытой клавиатурой (владелец
          2026-07-25): в шапке × слева, ✓ справа, заголовок = имя поля. Строки, которые
          менять нельзя (почта, Telegram, телефон), на тап ТРЯСУТСЯ и дают вибро-отклик. */}
      {/* Строки — НАБОРОМ (владелец 12.08.2026: вид пузырей и плиток значков со страницы
          «Уведомления» переносится на профиль): пузырь радиуса 26, плитка значка 34/11,
          значение серым под именем. Строки, которые менять нельзя, — без шеврона. */}
      <FPane className="prof-rows">
        <FNotifyList
          items={[
            {
              key: 'name',
              label: t('profileName'),
              ...side(name || t('profileName')),
              icon: <UserIcon />,
              // Плитка «Имени» — цветная, как у соседей и у родной строки (владелец
              // 09.09.2026; кадр iOS K02-profile): чёрная --ink выбивалась из ряда.
              iconColor: 'var(--violet)',
              onOpen: editName,
            },
            {
              key: 'email',
              label: t('profileEmail'),
              ...side(user.email),
              icon: <MailIcon />,
              iconColor: 'var(--blue)',
              chevron: false,
              onOpen: () => shakeProfileRow(1),
            },
            {
              key: 'tg',
              label: 'Telegram',
              ...side(user.tgName ?? t('telegramNone')),
              icon: <TgIcon />,
              iconColor: '#2AABEE',
              chevron: false,
              onOpen: () => shakeProfileRow(2),
            },
            {
              key: 'phone',
              label: t('profilePhone'),
              ...side(user.phone ?? t('phoneNone')),
              icon: <PhoneIcon />,
              iconColor: 'var(--green)',
              chevron: false,
              onOpen: () => shakeProfileRow(3),
            },
          ]}
        />
      </FPane>
      {busy && <DFine>{t('saving')}</DFine>}
      {/* Вместо фразы «изменить нельзя» — вход в передачу кабинета (заказ владельца
          2026-07-19): подпись + маленькая ссылка без рамок, как «Убрать фото».
          СЕЙЧАС ВХОДА НЕТ — «пока что» (владелец 31.08.2026, lib/hidden-features.ts):
          сама передача, её страница и ссылки целы. */}
      {!CABINET_TRANSFER_HIDDEN && (
        <>
          <DFine>{t('transferOffer')}</DFine>
          <DCenter>
            <KBareButton className="profedit-remove" onClick={onOpenTransfer}>
              {t('transferLink')}
            </KBareButton>
          </DCenter>
        </>
      )}
    </DGroup>
  );
}

// ─────────────── РОДНАЯ СТРАНИЦА «ПРОФИЛЬ» (этап Г плана) ───────────────
// В приложении со свежей сборкой профиль рисует НАТИВ: крупное фото героем, строки
// имя/почта/Telegram/телефон и «Выйти» внизу. Веб остаётся мозгом и делает всю работу:
// пишет имя (useSaveProfileName), кропает и заливает снимок прежней шторкой (Cropper),
// выходит из кабинета (useLogout). Тап по герою корня раздела ведёт сюда.

/** Ключи строк родной страницы (уникальны в пределах раздела «Аккаунт»). */
const PROF_ROW = {
  name: 'profile:name',
  photoRemove: 'profile:photo-remove',
  email: 'profile:email',
  tg: 'profile:tg',
  phone: 'profile:phone',
  logout: 'profile:logout',
  del: 'profile:delete',
} as const;

/**
 * РОДНАЯ СТРАНИЦА-ПРАВКА ИМЕНИ — БЕЗ АДРЕСА: в вебе имя правится БОКОВОЙ шторкой
 * (владелец 2026-07-25: «лучше горизонтальной»), а шторка адреса не меняла. В приложении
 * это ГОРИЗОНТАЛЬНАЯ страница (владелец 09.09.2026, канон Apple «Настройки → Основные →
 * Об этом устройстве → Имя»): «назад» слева, ✓ справа, поле в пузыре, автофокус и
 * клавиатура сразу. Нижним листом правка имени больше не открывается.
 *
 * INVARIANT(scriptera): ключ отсюда веб-шторку в приложении НЕ открывает.
 * Канон: docs/NATIVE-TABS-PLAN.md, волна 3.
 */
export const NATIVE_PROFILE_PAGE_KEYS: ReadonlySet<string> = new Set([PROF_ROW.name]);

/** Плитки значков строк: имя — `--violet` (владелец 09.09.2026, вместо чёрной `--ink`),
 *  почта синяя, Telegram фирменный #2AABEE, телефон зелёная. SF-символы того же рисунка. */
const PROF_ICON = {
  name: 'person',
  email: 'envelope',
  tg: 'paperplane.fill',
  phone: 'phone',
} as const;

export function useNativeProfilePage(
  user: ProfileInfo,
  /** Фото героя (data:image/png;base64) — то же, что у героя корня раздела. */
  photo: string,
  title: string,
): {
  pages: NativeAccountPage[];
  applySet: (rowKey: string, value: string | boolean) => boolean;
  tapRow: (rowKey: string) => boolean;
  /** Снимок из родного выбора фото. `cropped` — сборка уже обрезала его родным
   *  кроппером: тогда льём как есть, своего кропа не показываем. */
  onPhoto: (dataUrl: string, cropped?: boolean) => void;
} {
  const t = useTranslations('cabinet.settings');
  const tc = useTranslations('cabinet');
  const drawer = useSlidePanel();
  const saveName = useSaveProfileName();
  const { logout } = useLogout();
  const cropRef = useRef<(() => void) | null>(null);
  // Имя держим у себя: серверная карточка перечитывается кругом (router.refresh), а
  // строка обязана показать новое имя сразу после сохранения — без скачка «старое →
  // новое» через полсекунды.
  const [name, setName] = useState(user.name);
  // Те же три состояния, что у веб-редактора: «сохраняю…» мелким и ошибка красным.
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  // Фото есть или его убрали — состояние держим у себя, как веб-блок AvatarBlock:
  // строка «Убрать фото» обязана исчезнуть в тот же миг.
  const [hasAvatar, setHasAvatar] = useState(user.hasAvatar);
  // «Удалить учётную запись» (ТЗ П127.1): подтверждение — ДВУМЯ НАЖАТИЯМИ, тем же ходом,
  // что «Удалить контакт» (contacts.tsx, `ct:del`): подпись строки меняется на «Точно
  // удалить?», второй тап — делает. Своего системного окна тут не показываем.
  const [delSure, setDelSure] = useState(false);
  const delSureRef = useRef(false);
  delSureRef.current = delSure;
  const [delErr, setDelErr] = useState(false);
  useEffect(() => {
    setName(user.name);
  }, [user.name]);
  useEffect(() => {
    setHasAvatar(user.hasAvatar);
  }, [user.hasAvatar]);

  const page: NativeAccountPage = {
    key: 'profile',
    title,
    // Герой профиля — САМА ФОТОГРАФИЯ (в вебе от героя тоже остаётся только она,
    // владелец 2026-07-25), тап по ней открывает родной выбор снимка.
    hero: {
      color: accountRoleHex('var(--violet)'),
      title,
      meta: t('avatarTap'),
      photo,
      initial: user.initial,
      pick: true,
      // ГОТОВЫЕ СЛОВА РОДНОГО КРОППЕРА (волна 2, Опус-В2Г): экран обрезки встаёт сразу
      // после выбора снимка, и слов у него своих нет — считает их веб, как и всё
      // остальное. Пояснение то же, что у веб-кроппера: своих текстов не заводим.
      pickTitle: t('avatar'),
      pickHint: t('avatarCropHint'),
      pickDone: t('avatarSave'),
      pickCancel: tc('cancel'),
    },
    // ОШИБКА СОХРАНЕНИЯ — красным под шапкой, ровно как в вебе (DError над телом).
    ...(err ? { error: t('profileSaveError') } : {}),
    groups: [
      // «Убрать фото» — под фотографией, как в вебе; вопрос «Да/Нет» задаёт то же
      // подтверждение кабинета (в приложении это родное окно iOS).
      ...(hasAvatar
        ? [
            {
              key: 'photo',
              fields: [
                { key: PROF_ROW.photoRemove, kind: 'button' as const, title: t('avatarRemove') },
              ],
            },
          ]
        : []),
      {
        key: 'me',
        // «Сохраняю…» мелким под пузырём — как DFine под строками веб-редактора.
        ...(busy ? { status: { text: t('saving') } } : {}),
        fields: [
          {
            key: PROF_ROW.name,
            kind: 'nav',
            title: t('profileName'),
            icon: PROF_ICON.name,
            // Плитка «Имени» — цветная, как у соседей (владелец 09.09.2026): чёрная
            // `--ink` выбивалась из ряда пастельных плиток строк.
            color: accountRoleHex('var(--violet)'),
            value: name || t('profileName'),
          },
          // Почта, Telegram и телефон — ЗАКРЫТЫЕ строки: шеврона у них нет, а на тап
          // они трясутся и дают вибро (в вебе ровно то же — shakeRow).
          {
            key: PROF_ROW.email,
            kind: 'value',
            title: t('profileEmail'),
            icon: PROF_ICON.email,
            color: accountRoleHex('var(--blue)'),
            value: user.email,
            shake: true,
          },
          {
            key: PROF_ROW.tg,
            kind: 'value',
            title: 'Telegram',
            icon: PROF_ICON.tg,
            // Фирменный цвет Телеграма — не наша палитра, его не перекрашиваем.
            color: '#2AABEE',
            value: user.tgName ?? t('telegramNone'),
            shake: true,
          },
          {
            key: PROF_ROW.phone,
            kind: 'value',
            title: t('profilePhone'),
            icon: PROF_ICON.phone,
            color: accountRoleHex('var(--green)'),
            value: user.phone ?? t('phoneNone'),
            shake: true,
          },
        ],
      },
      {
        // «Выйти» — внизу профиля (владелец 01.09.2026): у корня раздела кнопки выхода
        // нет. Вопрос-подтверждение родной (askNativeConfirm), сам выход ведёт веб.
        key: 'out',
        fields: [
          { key: PROF_ROW.logout, kind: 'button', title: tc('account.signOut'), tone: 'danger' },
        ],
      },
      {
        // «Удалить учётную запись» — ПОСЛЕДНЕЙ группой (ТЗ П127.1; требование Apple
        // 5.1.1(v)). Сноска под пузырём говорит про 14 дней и про то, что вход её
        // возвращает, — человек читает это ДО нажатия, а не в окне после.
        key: 'del',
        hint: delErr
          ? t('deleteAccountError')
          : t('deleteAccountText', { days: ACCOUNT_DELETE_GRACE_DAYS }),
        ...(delErr ? { tone: 'danger' as const } : {}),
        fields: [
          {
            key: PROF_ROW.del,
            kind: 'button',
            title: delSure ? t('deleteAccountSure') : t('deleteAccount'),
            tone: 'danger',
          },
        ],
      },
    ],
  };

  /** Страница правки имени: поле с фокусом, «назад» слева и ✓ справа (см. инвариант выше). */
  const namePage: NativeAccountPage = {
    key: PROF_ROW.name,
    title: t('profileName'),
    edit: {
      key: PROF_ROW.name,
      value: name,
      placeholder: t('profileName'),
      done: t('avatarSave'),
    },
    // ПРАВКА ИМЕНИ — КАК У APPLE (владелец 09.09.2026; Настройки → Основные → Об этом
    // устройстве → Имя): ГОРИЗОНТАЛЬНАЯ страница — шаг вглубь того же «Профиля», слева
    // «назад», справа ✓, поле в пузыре с клавиатурой сразу. Прежняя вертикальная шторка
    // (present: 'sheet') этим решением отменена.
    present: 'push',
    groups: [],
  };

  /** Убрать фото — той же ручкой, что веб-блок AvatarBlock (второй руки нет). */
  const removePhoto = async () => {
    const r = await fetch(AVATAR_URL, { method: 'DELETE' })
      .then((res) => res.json())
      .catch(() => ({ ok: false }));
    if (!r.ok) return;
    bumpAvatar();
    setHasAvatar(false);
  };

  const applySet = (rowKey: string, value: string | boolean): boolean => {
    if (rowKey !== PROF_ROW.name || typeof value !== 'string') return false;
    const next = value.trim();
    // Пустое имя не сохраняем (та же мера, что у шторки правки) и строку не портим.
    if (!next || next === name) return true;
    setName(next);
    setBusy(true);
    setErr(false);
    void saveName(next).then((ok) => {
      setBusy(false);
      if (ok) return;
      // Не вышло — возвращаем прежнее имя и говорим об этом словами, как веб-редактор.
      setName(user.name);
      setErr(true);
    });
    return true;
  };

  const tapRow = (rowKey: string): boolean => {
    if (rowKey === PROF_ROW.photoRemove) {
      if (!hasNativeConfirm()) {
        void removePhoto();
        return true;
      }
      void askNativeConfirm({
        question: t('avatarRemoveQ'),
        confirmLabel: t('avatarRemove'),
        cancelLabel: tc('cancel'),
        destructive: false,
      }).then((yes) => {
        if (yes) void removePhoto();
      });
      return true;
    }
    if (rowKey === PROF_ROW.del) {
      // Первый тап — только подпись «Точно удалить?»; делает второй.
      if (!delSureRef.current) {
        setDelSure(true);
        return true;
      }
      setDelErr(false);
      void (async () => {
        type DelRes = { ok?: boolean; date?: string; error?: string };
        // Почта подтверждения (ТЗ ACCOUNTS-TOGGLE §3.6): в приложении подтверждение прежнее (родной
        // «Точно удалить?»), сервер ждёт почту учётной записи — её и шлём.
        const res = await fetch('/api/user/delete', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email: user.email }),
        }).catch(() => null);
        const data: DelRes = res ? await (res.json() as Promise<DelRes>).catch(() => ({ ok: false })) : { ok: false };
        if (res && isStepUp(res, data)) {
          // Родной экран: системное окно «войдите заново» (FUNNEL-TZ §5.2 п.7).
          setDelSure(false);
          void askNativeConfirm({ question: t('stepUpTitle'), message: t('stepUpBody'), confirmLabel: t('stepUpLogout'), cancelLabel: t('stepUpLater'), destructive: false }).then((yes) => {
            if (yes) void logout({ to: '/login' });
          });
          return;
        }
        if (!data.ok || !data.date) {
          setDelSure(false);
          setDelErr(true);
          return;
        }
        // Выход — той же рукой, что кнопка «Выйти»; вход встретит плашкой про день.
        await logout({ to: `/login?deleted=${encodeURIComponent(data.date)}` });
      })();
      return true;
    }
    if (rowKey !== PROF_ROW.logout) return false;
    if (!hasNativeConfirm()) {
      void logout();
      return true;
    }
    void askNativeConfirm({
      question: tc('account.signOutConfirm'),
      confirmLabel: tc('account.signOut'),
      cancelLabel: tc('cancel'),
      destructive: false,
    }).then((yes) => {
      if (yes) void logout();
    });
    return true;
  };

  // Снимок приехал строкой data:image/jpeg;base64. Обрезала его сборка сама (родной
  // кроппер, тот же квадрат 512) — льём готовое ЕДИНСТВЕННОЙ рукой заливки, без второго
  // кропа поверх. Не обрезала (старая сборка) — всё как у веб-страницы: кроппер шторкой
  // поверх, заливка внутри него той же рукой.
  const onPhoto = (dataUrl: string, cropped?: boolean) => {
    if (cropped) {
      void saveAvatarDataUrl(dataUrl);
      return;
    }
    void (async () => {
      const blob = await fetch(dataUrl)
        .then((r) => r.blob())
        .catch(() => null);
      if (!blob) return;
      const file = new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' });
      drawer.open({
        title: t('avatar'),
        // Кроппер — вертикальный лист в браузере (iOS PhotoCropSheet, ТЗ WEB-LOOK Э2.5).
        ...webOnly({ sheet: true }),
        ...(readPhoneLook() ? {} : {}),
        actionLabel: t('avatarSave'),
        actionIcon: 'checkmark',
        onAction: () => cropRef.current?.(),
        body: <Cropper file={file} saveRef={cropRef} onDone={() => drawer.close()} />,
      });
    })();
  };

  return { pages: [page, namePage], applySet, tapRow, onPhoto };
}

// ─── «Передать кабинет»: ссылка-приглашение (одна живая на кабинет) ───
interface TransferState {
  url: string;
  createdAt: string;
}

function CabinetTransferPanel() {
  const t = useTranslations('cabinet.settings');
  const { data, loading, mutate } = usePersistentData<{
    ok: boolean;
    transfer: TransferState | null;
  }>('cabinet:transfer', '/api/cabinet/transfer', { accept: (j) => !!(j as { ok?: boolean })?.ok });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  const transfer = data?.transfer ?? null;

  const create = async () => {
    if (busy) return;
    setBusy(true);
    setErr(false);
    const r = await fetch('/api/cabinet/transfer', { method: 'POST' })
      .then((res) => res.json() as Promise<{ ok?: boolean; transfer?: TransferState }>)
      .catch(() => ({ ok: false }) as { ok: boolean; transfer?: TransferState });
    setBusy(false);
    if (r.ok && r.transfer) mutate({ ok: true, transfer: r.transfer });
    else setErr(true);
  };

  const revoke = async () => {
    if (busy) return;
    setBusy(true);
    setErr(false);
    const r = await fetch('/api/cabinet/transfer', { method: 'DELETE' })
      .then((res) => res.json() as Promise<{ ok?: boolean }>)
      .catch(() => ({ ok: false }));
    setBusy(false);
    if (r.ok) mutate({ ok: true, transfer: null });
    else setErr(true);
  };

  return (
    <DGroup>
      {err && <DError>{t('transferError')}</DError>}
      <DHint>{t('transferIntro')}</DHint>
      <DFine>{t('transferHow')}</DFine>
      <DFine>{t('transferTip')}</DFine>
      {loading ? (
        <Sk w={260} h={16} />
      ) : !transfer ? (
        <div className="cab-transfer-confirm">
          <ConfirmInline
            label={busy ? t('transferCreating') : t('transferCreate')}
            question={t('transferCreateQ')}
            confirmLabel={t('transferCreate')}
            triggerClassName="btn primary"
            busy={busy}
            onConfirm={() => void create()}
          />
        </div>
      ) : (
        <>
          {/* Ссылка передачи — пузырём набора (FORM-DEBT, канон «Элементы формы»):
              название пузыря = имя единственного поля (FIn тут не нужен), пояснение
              «ссылка одноразовая» — ПОД полем внутри пузыря (владелец 11.08: описание
              всегда снизу); прежним отдельным DFine под кнопкой оно больше не висит. */}
          <FPane>
            <FBlock label={t('transferUrlLabel')} hint={t('transferUrlFine')}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <KInput
                  type="text"
                  readOnly
                  value={transfer.url}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <CopyBtn value={transfer.url} label={t('transferCopy')} />
              </div>
            </FBlock>
          </FPane>
          <div className="cab-transfer-confirm">
            <ConfirmInline
              label={t('transferRevoke')}
              question={t('transferRevokeQ')}
              confirmLabel={t('transferRevoke')}
              triggerClassName="btn sm"
              busy={busy}
              onConfirm={() => void revoke()}
            />
          </div>
        </>
      )}
    </DGroup>
  );
}
